import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Backdrop,
  Button,
  CircularProgress,
  Chip,
} from "@mui/material";
import { PersonAdd, UploadFile } from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import axiosInstance from "../../../api/axiosInstance";
import useNotification from "../../common/Notification";
import base_url from "../../../constants";

const CARD_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  maxWidth: 820,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 3,
  p: 4,
};

const WhatsappBusinessGroupEdit = ({
  open,
  onClose,
  options = [],
  initialGroupName = "",
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [searchedGName, setSeachedGName] = useState("");
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({
    name: false,
    file: false,
  });

  const notify = useNotification();

  useEffect(() => {
    if (open) {
      setSeachedGName(initialGroupName || "");
      setFile(null);
      setErrors({ name: false, file: false });
    }
  }, [open, initialGroupName]);

  const handleClose = () => {
    setSeachedGName("");
    setFile(null);
    setErrors({ name: false, file: false });
    if (onClose) onClose();
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile || null);
    setErrors((prev) => ({ ...prev, file: false }));
  };

  const handleSelectionChange = (e) => {
    setSeachedGName(e.target.value);
    setErrors((prev) => ({ ...prev, name: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isNameValid = searchedGName.trim() !== "";
    const isFileValid = file !== null;

    if (!isNameValid || !isFileValid) {
      setErrors({
        name: !isNameValid,
        file: !isFileValid,
      });
      return;
    }

    const formData = new FormData();
    formData.append("group_name", searchedGName);
    formData.append("file", file);

    try {
      setLoading(true);
      const res = await axiosInstance.post(
        `${base_url}/whatsapp-business/groups/update`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      notify("Contacts added successfully", "success");
      handleClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      if (err.response?.status === 404) {
        notify(
          err.response?.data?.message || "Group not found",
          "error"
        );
        return;
      }
      if (err.response?.status === 422) {
        notify(
          err.response?.data?.message || "Some duplicates found",
          "warning"
        );
        return;
      }
      notify("Something went wrong please try again!", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      aria-labelledby="add-contacts-modal-title"
      aria-describedby="add-contacts-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(15,23,42,0.55)",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={CARD_STYLE}>
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          backgroundColor: "rgba(25,118,210,0.12)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PersonAdd sx={{ fontSize: 20, color: "primary.main" }} />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          letterSpacing: "-0.02em",
                        }}
                      >
                        Add Contacts to Group
                      </Typography>
                    </Box>
                    <Button
                      onClick={handleClose}
                      size="small"
                      sx={{ minWidth: "auto" }}
                    >
                      ✕
                    </Button>
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                  >
                    Select a group and upload an Excel or CSV file with phone
                    numbers to add contacts.
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Group:
                    </Typography>
                    {searchedGName ? (
                      <Chip
                        size="small"
                        label={searchedGName}
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="error">
                        Group name is missing
                      </Typography>
                    )}
                  </Box>
                  {errors.name && !searchedGName && (
                    <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                      Please select a group
                    </Typography>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      mt: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "rgba(25,118,210,0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1.5,
                      }}
                    >
                      <UploadFile sx={{ fontSize: 30, color: "primary.main" }} />
                    </Box>
                    <Button
                      type="button"
                      variant="outlined"
                      color="primary"
                      sx={{ borderRadius: 2, mb: 1 }}
                      component="label"
                    >
                      <UploadFile sx={{ fontSize: 18, mr: 1 }} />
                      Choose Excel / CSV File
                      <input
                        type="file"
                        hidden
                        accept=".xls, .xlsx, .csv"
                        onChange={handleFileChange}
                      />
                    </Button>
                    {file && (
                      <Typography variant="body2">
                        Selected file: {file.name}
                      </Typography>
                    )}
                    {errors.file && (
                      <Typography
                        color="error"
                        variant="body2"
                        sx={{ mt: 0.5 }}
                      >
                        Please select an Excel or CSV file
                      </Typography>
                    )}
                    <Typography
                      sx={{
                        mt: 1.5,
                        fontSize: "12px",
                        color: "text.secondary",
                      }}
                    >
                      <span style={{ fontWeight: "bold" }}>Note: </span>
                      Upload a sheet with a <code>phone_number</code> column.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 2,
                    }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      component={motion.button}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        position: "relative",
                        backgroundColor: "#111827",
                        "&:hover": {
                          backgroundColor: "#020617",
                        },
                      }}
                      disabled={loading}
                    >
                      Submit
                      {loading ? (
                        <CircularProgress
                          size={24}
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            marginTop: "-12px",
                            marginLeft: "-12px",
                          }}
                        />
                      ) : null}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  );
};

export default WhatsappBusinessGroupEdit;
